<script lang="ts">
  import { untrack } from 'svelte';
  import { api } from '$shared/api';
  import { getCurrentRoute } from '$lib/router.svelte';
  import Avatar from '$shared/Avatar.svelte';
  import LoadingSkeleton from '$shared/LoadingSkeleton.svelte';
  import { weekEndLabel } from '$lib/utils/weekLabel';

  interface PlayerEntry {
    rank: number;
    user_id: number;
    username: string;
    avatar_url: string | null;
    value: number;
    points?: number;
  }
  interface Genre {
    id: number;
    name: string;
  }

  // Exactly three periods (week rework, 4A segmented control) — the rolling
  // `month`/`week` options are dropped from the UI; the API still accepts
  // them for legacy clients (docs/product-decisions.md #30/#31).
  const periods = [
    { value: 'season', label: 'This Week' },
    { value: 'last_week', label: 'Last Week' },
    { value: 'all', label: 'All Time' },
  ];

  let seasonInfo = $state<{ number: number; days_remaining: number; end_date: string } | null>(null);

  // Honor a deep-linked filter from the URL (#/leaderboard?period=all | ?season=current).
  const route = $derived(getCurrentRoute());

  let loading = $state(true);
  let genres = $state<Genre[]>([]);

  // Players state
  let playerGenre = $state(0);
  let playerPeriod = $state(
    untrack(() => {
      const q = new URLSearchParams(route.query);
      const p = q.get('period');
      // Legacy `week`/`month` (and anything else) fall back to the default
      // "This Week" segment — the segmented control only exposes these two.
      if (p === 'last_week' || p === 'all') return p;
      return 'season';
    }),
  );
  let players = $state<PlayerEntry[]>([]);

  // Load genres once
  $effect(() => {
    api
      .get<Genre[]>('/genres')
      .then((d) => {
        genres = Array.isArray(d) ? d : [];
      })
      .catch((e) => console.warn('Leaderboard genres load failed:', e));
  });

  // Fetch data when filters change
  $effect(() => {
    fetchPlayers();
  });

  async function fetchPlayers() {
    loading = true;
    try {
      let url =
        playerPeriod === 'season'
          ? '/leaderboard?limit=50&season=current'
          : `/leaderboard?limit=50&period=${playerPeriod}`;
      if (playerGenre > 0) url += `&genre_id=${playerGenre}`;
      const res = await api.get<{
        players: PlayerEntry[];
        season?: { number: number; days_remaining: number; end_date: string };
      }>(url);
      players = (res.players ?? []).map((p, i) => ({
        ...p,
        value: p.points ?? 0,
        rank: p.rank ?? i + 1,
      }));
      seasonInfo = res.season ?? null;
    } catch {
      players = [];
    }
    loading = false;
  }

  function rankDisplay(rank: number): string {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return `#${rank}`;
  }

  function formatPlayerValue(entry: PlayerEntry): string {
    return entry.value.toLocaleString();
  }
</script>

<h1>Leaderboard</h1>

<!-- Filters -->
<div class="filters">
  <select class="filter" bind:value={playerGenre} aria-label="Filter by genre">
    <option value={0}>All Genres</option>
    {#each genres as g}<option value={g.id}>{g.name}</option>{/each}
  </select>
  <div class="period-toggle" role="radiogroup" aria-label="Filter by time period">
    {#each periods as p}
      <button
        type="button"
        class="period-btn"
        class:period-btn--active={playerPeriod === p.value}
        aria-pressed={playerPeriod === p.value}
        onclick={() => (playerPeriod = p.value)}
      >
        {p.label}
      </button>
    {/each}
  </div>
  {#if seasonInfo && playerPeriod === 'season'}
    <span class="season-badge">Week {seasonInfo.number} · {weekEndLabel(seasonInfo.end_date)}</span>
  {/if}
</div>

<!-- Results -->
{#if loading}
  <LoadingSkeleton lines={8} />
{:else if players.length === 0}
  <div class="empty-state">
    <div class="empty-icon">🏆</div>
    <p>No rankings yet</p>
  </div>
{:else}
  <div class="lb-table">
    <div class="lb-header">
      <span class="lb-col-rank">Rank</span>
      <span class="lb-col-player">Player</span>
      <span class="lb-col-value">Points</span>
    </div>
    {#each players as entry}
      <a href="#/users/{entry.username}" class="lb-row">
        <span class="lb-col-rank">{rankDisplay(entry.rank)}</span>
        <span class="lb-col-player">
          <Avatar username={entry.username} avatarUrl={entry.avatar_url} size="xs" />
          <span class="lb-username">{entry.username}</span>
        </span>
        <span class="lb-col-value lb-value">{formatPlayerValue(entry)}</span>
      </a>
    {/each}
  </div>
{/if}

<style>
  h1 {
    margin-bottom: 1rem;
  }

  .filters {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-wrap: wrap;
    margin-bottom: 1.25rem;
  }
  .filter {
    padding: 0.4rem 0.75rem;
    font-size: 0.8rem;
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    color: var(--text);
    cursor: pointer;
  }

  /* Segmented 3-way period control (week rework, 4A) — same pill idiom as
     Points.svelte's .scope-toggle/.scope-btn. */
  .period-toggle {
    display: inline-flex;
    gap: 0.25rem;
    padding: 0.25rem;
    border: 1px solid var(--border);
    border-radius: 999px;
    background: var(--surface);
  }
  .period-btn {
    padding: 0.4rem 0.9rem;
    font-size: 0.8rem;
    font-weight: 600;
    color: var(--text-secondary);
    background: none;
    border: none;
    border-radius: 999px;
    cursor: pointer;
  }
  .period-btn--active {
    color: #fff;
    background: var(--pink);
  }

  .season-badge {
    display: inline-flex;
    align-items: center;
    padding: 0.3rem 0.75rem;
    font-size: 0.75rem;
    font-weight: 600;
    background: rgba(0, 229, 255, 0.1);
    color: var(--cyan);
    border-radius: 100px;
    white-space: nowrap;
  }

  .empty-state {
    text-align: center;
    padding: 3rem 1rem;
    color: var(--text-muted);
  }
  .empty-icon {
    font-size: 3rem;
    margin-bottom: 0.75rem;
  }

  .lb-table {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    overflow: hidden;
  }
  .lb-header {
    display: grid;
    grid-template-columns: 50px 1fr 80px;
    padding: 0.6rem 1rem;
    border-bottom: 1px solid var(--border);
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: var(--text-muted);
    font-weight: 600;
  }

  .lb-row {
    display: grid;
    grid-template-columns: 50px 1fr 80px;
    padding: 0.5rem 1rem;
    align-items: center;
    border-bottom: 1px solid var(--border);
    transition: background 0.15s;
  }
  .lb-row:last-child {
    border-bottom: none;
  }
  .lb-row:hover {
    background: var(--surface-2);
  }

  .lb-col-rank {
    font-weight: 700;
    font-size: 0.85rem;
  }
  .lb-col-player {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    min-width: 0;
  }
  .lb-col-value {
    text-align: right;
  }

  .lb-username {
    font-weight: 500;
    color: var(--text);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .lb-value {
    font-weight: 600;
    font-size: 0.85rem;
    color: var(--cyan);
  }

  @media (max-width: 480px) {
    .lb-header,
    .lb-row {
      padding: 0.4rem 0.75rem;
    }
  }
</style>
